// Medição > Visitas do site: Google Analytics 4 da empresa (conexão, escolha da propriedade e resumo).
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BarChart3, Info, RefreshCw, Unplug } from 'lucide-react'
import { ANALYTICS_DIM_LABELS, type AnalyticsCampaignsDto, type AnalyticsDim, type AnalyticsSummaryDto, type AnalyticsTotals } from '@impulso/shared/api/analytics'
import { errorMessage } from '../../../lib/api'
import { useCan, useCompany } from '../../../lib/company'
import { Delta, Empty, ErrorState, Loading, PageHead, Tabs } from '../../../ui'
import { useConfirm } from '../../../ui/confirm'
import { useToast } from '../../../ui/toast'
import { money } from '../../../features/billing/api'

const inCurrency = (v: number, currency: string) => v.toLocaleString('pt-BR', { style: 'currency', currency })
import { relativeTime } from '../../../features/tracking/components'
import { useAnalyticsAction, useAnalyticsCampaigns, useAnalyticsProperties, useAnalyticsSummary, useConnectAnalytics } from '../../../features/analytics/api'
import '../../../features/tracking/tracking.css'

const num = (n: number) => n.toLocaleString('pt-BR')
const change = (cur: number, prev: number) => {
  const v = prev > 0 ? cur / prev - 1 : null
  return v != null && Math.abs(v) >= 0.005 ? v : null // abaixo de 0,5% não vale mostrar (sairia "-0%")
}
const duration = (s: number) => (s >= 60 ? `${Math.floor(s / 60)} min ${s % 60}s` : `${s}s`)

export default function AnalyticsPage() {
  const company = useCompany()
  const [params, setParams] = useSearchParams()
  const [days, setDays] = useState<'7' | '30' | '90'>('30')
  const q = useAnalyticsSummary(company.id, Number(days))
  const erro = params.get('erro')
  const escolher = params.get('escolher')
  const clear = () => setParams({}, { replace: true })

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Visitas do site" sub="Os números do Google Analytics do seu site, junto com os anúncios: de onde vêm as visitas e o que vira contato." />
      {erro && <div className="form-error" role="alert">{erro}</div>}
      {params.get('conectado') && <div className="form-ok" role="status">Google Analytics conectado! Estamos trazendo os últimos 13 meses.</div>}
      {escolher ? <PropertyPicker credentialId={escolher} onDone={clear} />
        : q.isLoading ? <Loading lines={6} />
          : q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} />
            : !q.data!.source ? <ConnectCard />
              : <Summary s={q.data!} days={days} setDays={setDays} />}
      {!escolher && !q.isLoading && !q.error && <CampaignsCross days={Number(days)} />}
    </div>
  )
}

function ConnectCard() {
  const company = useCompany()
  const canConnect = useCan('company.manage_connections')
  const connect = useConnectAnalytics(company.id)
  return (
    <div className="card stack" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 12 }}>
        <span className="ic purple"><BarChart3 size={20} /></span>
        <div><h2>Conecte o Google Analytics</h2><p className="small muted">Usamos só leitura: o Seu Impulso não altera nada na sua conta do Google.</p></div>
      </div>
      <ul className="small" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
        <li>Visitas, pessoas e tempo no site, dia a dia</li>
        <li>De onde vêm as visitas: Instagram, Google, direto…</li>
        <li>Quais páginas e campanhas trazem mais contatos (eventos-chave)</li>
      </ul>
      <div className="callout small"><Info size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} /><span>Precisa ser o <b>Google Analytics 4</b> e a conta Google usada precisa ter acesso à propriedade (pelo menos como leitor). Pode ser outra conta, diferente da do Google Ads.</span></div>
      {canConnect
        ? <div><button className="btn primary" disabled={connect.isPending} onClick={() => connect.mutate()}>{connect.isPending ? 'Abrindo o Google…' : 'Conectar Google Analytics'}</button></div>
        : <p className="small muted">Peça para quem gerencia a empresa conectar.</p>}
      {connect.error && <div className="form-error">{errorMessage(connect.error)}</div>}
    </div>
  )
}

function PropertyPicker({ credentialId, onDone }: { credentialId: string; onDone: () => void }) {
  const company = useCompany()
  const q = useAnalyticsProperties(company.id, credentialId)
  const act = useAnalyticsAction(company.id)
  const toast = useToast()
  const [picked, setPicked] = useState('')
  if (q.isLoading) return <Loading lines={4} />
  if (q.error) return <ErrorState error={q.error} />
  return (
    <form className="card stack" style={{ gap: 12 }} onSubmit={(e) => {
      e.preventDefault()
      act.mutate({ kind: 'select', credentialId, propertyId: picked }, { onSuccess: () => { toast('Conectado! Estamos trazendo os últimos 13 meses.'); onDone() }, onError: (err) => toast(errorMessage(err)) })
    }}>
      <h2>Qual propriedade é do site desta empresa?</h2>
      <div className="stack" style={{ gap: 8 }} role="radiogroup" aria-label="Propriedade do Google Analytics">
        {q.data!.items.map((p) => (
          <label key={p.id} className={`option-card ${picked === p.id ? 'on' : ''}`} style={{ cursor: 'pointer' }}>
            <input type="radio" name="ga-prop" value={p.id} checked={picked === p.id} onChange={() => setPicked(p.id)} style={{ display: 'none' }} />
            <div><div className="strong">{p.name}</div><div className="small muted">{p.accountName} · {p.id.replace('properties/', 'ID ')}</div></div>
            <span className="radio" />
          </label>
        ))}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn primary" disabled={!picked || act.isPending}>{act.isPending ? 'Conectando…' : 'Conectar esta propriedade'}</button>
        <button type="button" className="btn ghost" onClick={onDone}>Cancelar</button>
      </div>
    </form>
  )
}

function Summary({ s, days, setDays }: { s: AnalyticsSummaryDto; days: '7' | '30' | '90'; setDays: (d: '7' | '30' | '90') => void }) {
  const company = useCompany()
  const canManage = useCan('company.manage_connections')
  const canSync = useCan('company.edit_drafts')
  const act = useAnalyticsAction(company.id)
  const connect = useConnectAnalytics(company.id)
  const toast = useToast()
  const confirm = useConfirm()
  const src = s.source!
  const c = s.current
  const p = s.previous
  const showRevenue = c.revenue > 0 || p.revenue > 0
  const kpis: Array<{ label: string; value: string; delta: number | null; hint: string }> = [
    { label: 'Sessões', value: num(c.sessions), delta: change(c.sessions, p.sessions), hint: 'Visitas ao site' },
    { label: 'Visitas de usuários', value: num(c.totalUsers), delta: change(c.totalUsers, p.totalUsers), hint: 'Soma das pessoas de cada dia: quem volta em outro dia conta de novo' },
    { label: 'Eventos-chave', value: num(c.keyEvents), delta: change(c.keyEvents, p.keyEvents), hint: 'Contatos, cadastros, compras…' },
    { label: 'Taxa de engajamento', value: `${Math.round(c.engagementRate * 100)}%`, delta: change(c.engagementRate, p.engagementRate), hint: `Tempo médio: ${duration(c.avgEngagementSeconds)}` },
    ...(showRevenue ? [{ label: 'Receita', value: money(c.revenue), delta: change(c.revenue, p.revenue), hint: 'Registrada no Google Analytics' }] : []),
  ]
  const max = Math.max(1, ...s.byDay.map((d) => d.sessions))
  return (
    <>
      <div className="card row between wrap" style={{ gap: 12 }}>
        <div>
          <div className="row wrap" style={{ gap: 8 }}>
            <h2>{src.propertyName}</h2>
            <span className={`badge ${src.status === 'active' ? 'green' : 'red'}`}>{src.status === 'active' ? 'Conectado' : 'Precisa reconectar'}</span>
          </div>
          <div className="small muted mt-8">
            {src.accountName ? `${src.accountName} · ` : ''}{src.connectedBy ? `autorizado por ${src.connectedBy} · ` : ''}atualizado {relativeTime(src.lastSyncAt)}
          </div>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {canSync && src.status === 'active' && <button className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ kind: 'sync' }, { onSuccess: () => toast('Atualizando… os números mudam em instantes.'), onError: (e) => toast(errorMessage(e)) })}><RefreshCw size={14} /> Atualizar</button>}
          {canManage && <button className="btn ghost sm" disabled={act.isPending} onClick={async () => { if (await confirm({ title: 'Desconectar o Google Analytics?', message: 'Os números importados serão apagados do Seu Impulso.', confirmLabel: 'Desconectar', danger: true })) act.mutate({ kind: 'disconnect' }, { onError: (e) => toast(errorMessage(e)) }) }}><Unplug size={14} /> Desconectar</button>}
        </div>
      </div>

      {src.status !== 'active' && (
        <div className="callout amber" role="alert">
          <span className="small"><b>Não conseguimos ler o Google Analytics.</b> {src.lastError ?? ''} {canManage && <button className="btn sm" style={{ marginLeft: 8 }} disabled={connect.isPending} onClick={() => connect.mutate()}>Reconectar</button>}</span>
        </div>
      )}
      {src.backfilling && <div className="callout small" role="status"><RefreshCw size={16} className="spin" style={{ flexShrink: 0 }} /><span>Trazendo os últimos 13 meses do Google Analytics. Os números vão aparecendo em alguns minutos.</span></div>}

      <div className="row between wrap" style={{ gap: 8 }}>
        <h2>Resumo</h2>
        <Tabs value={days} onChange={setDays} options={[{ value: '7', label: '7 dias' }, { value: '30', label: '30 dias' }, { value: '90', label: '90 dias' }]} />
      </div>
      {c.sessions === 0 && p.sessions === 0
        ? <Empty title="Sem visitas neste período">Se o site é novo ou a tag do Google Analytics ainda não está instalada, os números aparecem assim que as visitas chegarem.</Empty>
        : (
          <>
            <div className="grid g-4">
              {kpis.map((k) => (
                <div key={k.label} className="card kpi">
                  <div className="label">{k.label}</div>
                  <div className="value">{k.value}</div>
                  <div className="row" style={{ gap: 6 }}><Delta value={k.delta} /><span className="hint">{k.hint}</span></div>
                </div>
              ))}
            </div>
            <div className="card">
              <div className="bars" role="img" aria-label="Sessões e eventos-chave por dia">
                {s.byDay.map((d) => (
                  <div key={d.day} className="bar" data-tip={`${new Date(`${d.day}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}: ${d.sessions} sessões, ${d.keyEvents} eventos-chave`}>
                    <span className="v" style={{ height: `${(Math.max(0, d.sessions - d.keyEvents) / max) * 100}%` }} />
                    <span className="c" style={{ height: `${(d.keyEvents / max) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div className="legend mt-8"><span><i style={{ background: 'var(--primary-100)' }} />Sessões</span><span><i style={{ background: 'var(--primary)' }} />Eventos-chave</span></div>
            </div>
            <div className="grid g-2">
              {(['channel', 'campaign', 'landing_page', 'source_medium', 'key_event', 'device', 'city'] as AnalyticsDim[]).map((dim) => <TopCard key={dim} dim={dim} s={s} total={c} />)}
            </div>
          </>
        )}
    </>
  )
}

/** Anúncio × site: o que cada campanha gastou e o que virou visita e contato. */
function CampaignsCross({ days }: { days: number }) {
  const company = useCompany()
  const q = useAnalyticsCampaigns(company.id, days)
  const d: AnalyticsCampaignsDto | undefined = q.data
  if (q.isLoading) return <Loading lines={3} />
  if (q.error || !d) return null
  const dash = (v: number | null, f: (n: number) => string = num) => (v == null ? '–' : f(v))
  return (
    <div className="card flush">
      <div style={{ padding: '14px 16px' }}>
        <h2>Anúncios × site</h2>
        <p className="small muted">Quantos cliques viraram visita e quantos contatos chegaram pelas suas páginas, campanha por campanha (últimos {days} dias).</p>
      </div>
      {d.rows.length === 0 ? <p className="small muted" style={{ padding: '0 16px 16px' }}>Nenhuma campanha com gasto neste período.</p> : (
        <div className="table-wrap">
          <table className="table stack-sm">
            <thead><tr><th>Campanha</th><th className="right">Gasto</th><th className="right">Cliques</th><th className="right">Visitas</th><th className="right">Visitas por clique</th><th className="right">Contatos</th><th className="right">Custo por contato</th></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.campaignId}>
                  <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name}>{r.name}</td>
                  <td className="right nowrap" data-label="Gasto">{inCurrency(r.spend, r.currency)}</td>
                  <td className="right" data-label="Cliques">{num(r.clicks)}</td>
                  <td className="right" data-label="Visitas">{dash(r.sessions)}</td>
                  <td className="right" data-label="Visitas por clique">{dash(r.clickToVisit, (v) => `${Math.round(v * 100)}%`)}</td>
                  <td className="right" data-label="Contatos">{num(r.contacts)}</td>
                  <td className="right nowrap" data-label="Custo por contato">{dash(r.costPerContact, (v) => inCurrency(v, r.currency))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="small muted" style={{ padding: '12px 16px' }}>
        {d.hasAnalytics ? 'Visitas e contatos sem marcação de campanha não entram na tabela' : 'Conecte o Google Analytics para ver as visitas'}
        {d.unmatched.contacts > 0 || (d.unmatched.sessions ?? 0) > 0 ? `: ${[d.unmatched.sessions ? `${num(d.unmatched.sessions)} visitas` : '', d.unmatched.contacts ? `${num(d.unmatched.contacts)} contatos` : ''].filter(Boolean).join(' e ')} ficaram de fora.` : '.'}
        {' '}Campanhas criadas aqui já saem com a marcação (UTM) no link.
      </p>
    </div>
  )
}

function TopCard({ dim, s, total }: { dim: AnalyticsDim; s: AnalyticsSummaryDto; total: AnalyticsTotals }) {
  const rows = s.top[dim]
  const isEvent = dim === 'key_event'
  return (
    <div className="card flush">
      <div style={{ padding: '14px 16px' }}><h3>{ANALYTICS_DIM_LABELS[dim]}</h3></div>
      {rows.length === 0 ? <p className="small muted" style={{ padding: '0 16px 16px' }}>Nada neste período.</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{isEvent ? 'Evento' : 'Nome'}</th>{!isEvent && <th className="right">Sessões</th>}<th className="right">{isEvent ? 'Quantidade' : 'Eventos-chave'}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.key}>{r.key}</td>
                  {!isEvent && <td className="right nowrap">{num(r.sessions)} <span className="small muted">{total.sessions ? `${Math.round((r.sessions / total.sessions) * 100)}%` : ''}</span></td>}
                  <td className="right">{num(r.keyEvents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
